import { useLingui } from '@lingui/react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import TeamImageSection from '../components/TeamImageSection';

import {
  JobGroup,
  JobLevel,
  JobTitle,
  JobTitlesByGroup,
  useTeamEditor,
} from '../hooks/useTeamEditor';

export default function TeamPage() {
  const { i18n } = useLingui();
  const navigate = useNavigate();

  const {
    name,
    setName,
    role,
    setRole,
    expirationDate,
    setExpirationDate,
    imageUrls,
    imageUploading,
    publishState,
    isValid,
    lastSavedId,
    handleImageUpload,
    handlePublish,
  } = useTeamEditor();

  useEffect(() => {
    if (lastSavedId) navigate(`/team/edit/${lastSavedId}`);
  }, [lastSavedId, navigate]);

  const isLoading = publishState.status === 'loading';

  const availableJobTitles = role?.jobGroup
    ? JobTitlesByGroup[role.jobGroup]
    : Object.values(JobTitle);

  return (
    <div className="container-page padding-top-8 padding-bottom-8">
      <div className="col-xs-12 col-lg-6">
        <div className="flex flex-col gap-2 w-full">
          <h1>{i18n._('team.create')}</h1>

          <TeamImageSection
            onUpload={handleImageUpload}
            imageUrls={imageUrls}
            imageUploading={imageUploading}
          />

          <div className="border border-gray-tint-6 rounded-_20 padding-3 w-full">
            <h3>{i18n._('team.name')}</h3>

            <Input
              disabled={isLoading}
              value={name}
              maxlength={80}
              onChange={(v) => setName(v ?? '')}
              placeholder={i18n._('team.namePlaceholder')}
            />
          </div>

          <div className="border border-gray-tint-6 rounded-_20 padding-3 w-full">
            <h3>{i18n._('team.group')}</h3>
            <Select
              disabled={isLoading}
              placeholder={i18n._('team.groupPlaceholder')}
              value={role?.jobGroup ?? null}
              onChange={(groupValue) => {
                if (!groupValue) return;
                const newJobGroup = groupValue as JobGroup;

                setRole((prev) => ({
                  level: prev?.level ?? JobLevel.APPRENTICE,
                  jobGroup: newJobGroup,
                  jobTitle: JobTitlesByGroup[newJobGroup][0],
                }));
              }}
              options={[
                { label: 'Management-Team', value: JobGroup.MANAGEMENT },
                { label: 'Designer', value: JobGroup.DESIGNER },
                { label: 'Developer', value: JobGroup.DEVELOPER },
                { label: 'Social-Media Team', value: JobGroup.SOCIAL_MEDIA },
              ]}
            />
          </div>

          <div className="border border-gray-tint-6 rounded-_20 padding-3 w-full">
            <h3>{i18n._('team.level')}</h3>
            <Select
              disabled={isLoading}
              placeholder={i18n._('team.levelPlaceholder')}
              value={role?.level ?? null}
              onChange={(levelValue) => {
                if (!levelValue) return;

                setRole((prev) => ({
                  level: levelValue as JobLevel,
                  jobGroup: prev?.jobGroup ?? JobGroup.DEVELOPER,
                  jobTitle: prev?.jobTitle ?? JobTitle.BACKEND,
                }));
              }}
              options={[
                { label: 'Apprentice', value: JobLevel.APPRENTICE },
                {
                  label: 'Young Professional',
                  value: JobLevel.YOUNG_PROFESSIONAL,
                },
                { label: 'Professional', value: JobLevel.PROFESSIONAL },
                { label: 'Management', value: JobLevel.MANAGEMENT },
              ]}
            />
          </div>

          
            <div className="border border-gray-tint-6 rounded-_20 padding-3 w-full">
              <h3>{i18n._('team.expirationDate')}</h3>
              <Input
                disabled={isLoading}
                type="date"
                value={expirationDate}
                onChange={(v) => setExpirationDate(v ?? '')}
                placeholder={i18n._('team.expirationDatePlaceholder')}
                minDate='today'
              />
            </div>

          <div className="border border-gray-tint-6 rounded-_20 padding-3 w-full">
            <h3>{i18n._('team.role')}</h3>

            <Select
              disabled={isLoading}
              placeholder={i18n._('team.rolePlaceholder')}
              value={role?.jobTitle ?? null}
              onChange={(titleValue) => {
                if (!titleValue) return;

                setRole((prev) => ({
                  level: prev?.level ?? JobLevel.APPRENTICE,
                  jobGroup: prev?.jobGroup ?? JobGroup.DEVELOPER,
                  jobTitle: titleValue as JobTitle,
                }));
              }}
              options={availableJobTitles.map((title) => ({
                label: title,
                value: title,
              }))}
            />
          </div>

          <div className="flex gap-2 margin-top-2">
            <Button theme="secondary" disabled={isLoading}>
              {i18n._('common.cancel')}
            </Button>

            <Button onClick={handlePublish} disabled={!isValid || isLoading}>
              {isLoading ? i18n._('common.saving') : i18n._('common.save')}
            </Button>
          </div>

          {publishState.status === 'success' && lastSavedId && (
            <p style={{ color: 'green' }}>{i18n._('team.saveSuccess')}</p>
          )}

          {publishState.status === 'error' && (
            <p>{i18n._('team.saveError', { message: publishState.message })}</p>
          )}
        </div>
      </div>
    </div>
  );
}
